"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetworkApi = void 0;
const axios_1 = __importDefault(require("axios"));
const create_hash_1 = __importDefault(require("create-hash"));
const debug_1 = __importDefault(require("debug"));
const chain_config_1 = require("../../config/chain.config");
const network_helper_1 = require("../../helpers/network.helper");
const network_enum_1 = require("../../helpers/network.enum");
const no_nodes_found_exception_1 = require("./eceptions/no-nodes-found.exception");
const unknown_chain_exception_1 = require("./eceptions/unknown-chain.exception");
const hash_mismatch_exception_1 = require("./eceptions/hash-mismatch.exception");
const chain_unavailable_exception_1 = require("./eceptions/chain-unavailable.exception");
const no_nodes_to_query_exception_1 = require("./eceptions/no-nodes-to-query.exception");
const info = (0, debug_1.default)('info');
class NetworkApi {
    currentChain;
    currentNodes = [];
    isHTTPSNodesOnly = false;
    nodeIndex = 0;
    feeSettings;
    gasSettings;
    constructor(chain) {
        this.currentChain = chain;
    }
    load(params) {
        Object.assign(this, params);
    }
    upload() {
        const { currentChain, currentNodes, nodeIndex, feeSettings, gasSettings, } = this;
        return {
            currentChain, currentNodes, nodeIndex, feeSettings, gasSettings,
        };
    }
    static async getChainGlobalConfig() {
        const baseURL = 'https://raw.githubusercontent.com/thepower/all_chains/main/config.json';
        const { data } = await axios_1.default.request({ baseURL });
        return data;
    }
    static async getNetworkChains(networkName) {
        const chainGlobalConfig = await NetworkApi.getChainGlobalConfig();
        const networks = chainGlobalConfig.settings;
        const chainArray = networks[networkName];
        if (!chainArray) {
            throw new Error(`Chains not found for network ${networkName}`);
        }
        return chainArray;
    }
    static async getRandomChain(networkName) {
        const chainArray = await NetworkApi.getNetworkChains(networkName);
        const strChain = chainArray[Math.floor(Math.random() * chainArray.length)].toString();
        return Number(strChain);
    }
    async changeChain(chain, isHTTPSNodesOnly = false) {
        this.currentChain = chain;
        await this.bootstrap(isHTTPSNodesOnly);
    }
    setCurrentConfig = async (newNodes) => {
        let currentNodes = [];
        currentNodes = await (0, network_helper_1.queueNodes)(newNodes);
        if (!currentNodes.length) {
            currentNodes = await (0, network_helper_1.queueNodes)(newNodes, 5000);
        }
        this.currentNodes = currentNodes;
        this.nodeIndex = 0;
    };
    async sendTxAndWaitForResponse(tx, timeout = 120) {
        return this.sendPreparedTX(tx, timeout);
    }
    async getFeeSettings() {
        const settings = await this.askBlockchainTo(network_enum_1.ChainAction.GET_NODE_SETTINGS, {});
        return this.calculateFeeSettings(settings);
    }
    getBlockHash = async (height) => this.askBlockchainTo(network_enum_1.ChainAction.GET_BLOCK_HASH, { chain: this.currentChain, height });
    getBlock = async (hash = 'last') => this.askBlockchainTo(network_enum_1.ChainAction.GET_BLOCK, { chain: this.currentChain, hash });
    getBlockInfo = async (hash = 'last') => this.askBlockchainTo(network_enum_1.ChainAction.GET_BLOCK_INFO, { chain: this.currentChain, hash });
    getWallet = async (address) => this.askBlockchainTo(network_enum_1.ChainAction.GET_WALLET, { chain: this.currentChain, address });
    loadScCode = async (address) => new Uint8Array(await this.askBlockchainTo(network_enum_1.ChainAction.GET_SC_CODE, { chain: this.currentChain, address }));
    loadScState = async (address) => new Uint8Array(await this.askBlockchainTo(network_enum_1.ChainAction.GET_SC_STATE, { chain: this.currentChain, address }));
    loadScStateByKey = async (address, key) => new Uint8Array(await this.askBlockchainTo(network_enum_1.ChainAction.GET_SC_STATE_BY_KEY, { chain: this.currentChain, address, key }));
    getLstoreData = async (address, path) => this.askBlockchainTo(network_enum_1.ChainAction.GET_LSTORE, { chain: this.currentChain, address, path });
    getChain() {
        return this.currentChain;
    }
    getIsHTTPSNodesOnly() {
        return this.isHTTPSNodesOnly;
    }
    bootstrap = async (isHTTPSNodesOnly = false) => {
        const chainInfo = await NetworkApi.getChainGlobalConfig();
        const chainData = chainInfo.chains[this.currentChain];
        if (chainData) {
            this.isHTTPSNodesOnly = isHTTPSNodesOnly;
            const httpsRegExp = /^https:\/\//ig;
            const transformedNodeList = (0, network_helper_1.transformNodeList)(chainData);
            const nodesList = isHTTPSNodesOnly ? transformedNodeList.filter((node) => httpsRegExp.test(node.address)) : transformedNodeList;
            if (!transformedNodeList.length) {
                throw new no_nodes_found_exception_1.NoNodesFoundException(this.currentChain);
            }
            await this.setCurrentConfig(nodesList);
            info(`Bootstrapped chain ${this.currentChain}`, this.currentNodes);
            await this.loadFeeGasSettings();
            return;
        }
        throw new unknown_chain_exception_1.UnknownChainException(this.currentChain);
    };
    async loadRemoteSCInterface(interfaceData) {
        const [hashData, urlData] = interfaceData;
        const [hashAlg, hashValue] = hashData.split(':');
        const baseURL = urlData.includes('ipfs') ?
            `https://ipfs.io/ipfs/${urlData.split('://')[1]}` // TODO: what url is it? move to const (config)
            : `${urlData}?${+new Date()}`;
        const { data } = await axios_1.default.request({ baseURL, responseType: 'arraybuffer' });
        const binaryCode = new Uint8Array(data);
        const actualHash = (0, create_hash_1.default)(hashAlg).update(binaryCode).digest().toString('hex');
        if (actualHash !== hashValue) {
            throw new hash_mismatch_exception_1.HashMismatchException();
        }
        return binaryCode;
    }
    async sendPreparedTX(tx, timeout = 1000) {
        const response = await this.askBlockchainTo(network_enum_1.ChainAction.CREATE_TRANSACTION, { data: { tx } });
        return this.checkTransaction(response.txid, timeout);
    }
    calculateFeeSettings(settings) {
        let result = settings.current;
        let feeCur;
        if (result.fee) {
            result = result.fee;
            if (result.SK) {
                feeCur = 'SK';
            }
            else if (result.FEE) {
                feeCur = 'FEE';
            }
            else {
                return {};
            }
        }
        else {
            return {};
        }
        return {
            feeCur,
            fee: result[feeCur].base,
            baseEx: result[feeCur].baseextra,
            kb: result[feeCur].kb,
        };
    }
    checkTransaction = async (txId, timeout) => new Promise((resolve, reject) => {
        let callCount = 0;
        const check = () => setTimeout(async () => {
            try {
                callCount += 1;
                const status = await this.askBlockchainTo(network_enum_1.ChainAction.GET_TRANSACTION_STATUS, { txId });
                if (status) {
                    if (status?.error || status?.revert) {
                        reject(new Error(`${txId}: ${status?.res} ${status?.revert}`));
                    }
                    else if (status.retval) {
                        resolve({
                            txId, res: status.res, block: status.block, retval: status.retval,
                        });
                    }
                    else {
                        resolve({ txId, res: status.res, block: status.block });
                    }
                }
                else if (callCount < timeout) {
                    check();
                }
                else {
                    reject(new Error(`${txId}: Transaction status lost`));
                }
            }
            catch (e) {
                reject(e);
            }
        }, chain_config_1.config.callbackCallDelay);
        return check();
    });
    incrementNodeIndex = async () => {
        this.nodeIndex += 1;
        if (this.nodeIndex >= this.currentNodes.length || this.currentNodes[this.nodeIndex].time === chain_config_1.config.maxNodeResponseTime) {
            this.currentNodes = await (0, network_helper_1.queueNodes)(this.currentNodes);
            this.nodeIndex = 0;
            if (this.nodeIndex >= this.currentNodes.length || this.currentNodes[this.nodeIndex].time === chain_config_1.config.maxNodeResponseTime) {
                throw new chain_unavailable_exception_1.ChainUnavailableException();
            }
        }
    };
    httpRequest = async (actionUrl, parameters) => {
        const totalAttempts = chain_config_1.config.requestTotalAttempts;
        let success = false;
        let result;
        let i = 0;
        if (!this.currentNodes.length) {
            throw new no_nodes_to_query_exception_1.NoNodesToQueryException();
        }
        while (!success) {
            i += 1;
            /**
             * @todo move to env
             */
            parameters.baseURL = `${this.currentNodes[this.nodeIndex].address}/api${actionUrl}`;
            try {
                result = await axios_1.default.request(parameters);
                success = true;
            }
            catch (e) {
                if (e.response === undefined) {
                    // Server did not respond
                    if (i < totalAttempts) {
                        await this.incrementNodeIndex();
                    }
                    else {
                        throw new Error('Too many attempts.');
                    }
                }
                else {
                    // Server responded with error
                    throw new Error(e.response.data.msg);
                }
            }
        }
        return result.data;
    };
    checkResponseValidity(data) {
        if (!(data instanceof ArrayBuffer) && !(data instanceof Buffer)) {
            if (!data.ok) {
                if (data.msg) {
                    throw new Error(`(${data.code}) ${data.msg}`);
                }
                else {
                    throw new Error(`Incorrect response (${data.code})`);
                }
            }
        }
    }
    async getAddressChain(address) {
        return this.askBlockchainTo(network_enum_1.ChainAction.GET_MY_CHAIN, { address });
    }
    async getChainNodes(chain, remoteChain) {
        return this.askBlockchainTo(network_enum_1.ChainAction.GET_CHAIN_NODES, {
            chain,
            remoteChain,
        });
    }
    async getNodeSettings() {
        return this.askBlockchainTo(network_enum_1.ChainAction.GET_NODE_SETTINGS, {});
    }
    async createTransaction(data) {
        return this.askBlockchainTo(network_enum_1.ChainAction.CREATE_TRANSACTION, { data });
    }
    async getTransactionStatus(txId) {
        return this.askBlockchainTo(network_enum_1.ChainAction.GET_TRANSACTION_STATUS, { txId });
    }
    async askBlockchainTo(kind, parameters) {
        let actionUrl;
        const requestParams = {
            timeout: chain_config_1.config.chainRequestTimeout,
            method: 'get',
        };
        switch (kind) {
            case network_enum_1.ChainAction.GET_BLOCK_HASH:
                actionUrl = '/blockhash';
                requestParams.url = parameters.height.toString();
                break;
            case network_enum_1.ChainAction.GET_BLOCK:
                actionUrl = '/block';
                requestParams.url = parameters.hash;
                break;
            case network_enum_1.ChainAction.GET_BLOCK_INFO:
                actionUrl = '/blockinfo';
                requestParams.url = parameters.hash;
                break;
            case network_enum_1.ChainAction.GET_WALLET:
                actionUrl = '/address';
                requestParams.url = parameters.address;
                break;
            case network_enum_1.ChainAction.CREATE_TRANSACTION:
                actionUrl = '/tx/new';
                requestParams.method = 'post';
                requestParams.data = parameters.data;
                break;
            case network_enum_1.ChainAction.GET_TRANSACTION_STATUS:
                actionUrl = '/tx/status';
                requestParams.url = parameters.txId;
                break;
            case network_enum_1.ChainAction.GET_MY_CHAIN:
                actionUrl = '/where';
                requestParams.url = parameters.address;
                break;
            case network_enum_1.ChainAction.GET_CHAIN_NODES:
                actionUrl = '/nodes';
                requestParams.url = parameters.remoteChain.toString();
                break;
            case network_enum_1.ChainAction.GET_NODE_SETTINGS:
                actionUrl = '/settings';
                break;
            case network_enum_1.ChainAction.GET_SC_CODE:
                requestParams.responseType = 'arraybuffer';
                requestParams.url = `${parameters.address}/code`;
                actionUrl = '/address';
                break;
            case network_enum_1.ChainAction.GET_SC_STATE:
                requestParams.responseType = 'arraybuffer';
                requestParams.url = `${parameters.address}/state`;
                actionUrl = '/address';
                break;
            case network_enum_1.ChainAction.GET_SC_STATE_BY_KEY:
                requestParams.responseType = 'arraybuffer';
                requestParams.url = `${parameters.address}/state/0x${parameters.key}`;
                actionUrl = '/address';
                break;
            case network_enum_1.ChainAction.GET_LSTORE:
                requestParams.responseType = 'arraybuffer';
                requestParams.url = `${parameters.address}/lstore/${parameters.path}`;
                actionUrl = '/address';
                break;
            default:
                throw new Error('Unknown action');
        }
        let response = await this.httpRequest(actionUrl, requestParams);
        this.checkResponseValidity(response);
        response = (0, network_helper_1.transformResponse)(response, kind, requestParams);
        return response;
    }
    async loadFeeGasSettings() {
        const settings = await this.askBlockchainTo(network_enum_1.ChainAction.GET_NODE_SETTINGS, {});
        this.feeSettings = this.calculateFeeSettings(settings);
        this.gasSettings = this.calculateGasSettings(settings);
    }
    calculateGasSettings(settings) {
        let result = settings.current;
        let gasCur;
        if (result.gas) {
            result = result.gas;
            gasCur = result.SK ? 'SK' : 'FEE';
        }
        else {
            return {};
        }
        return {
            gasCur,
            gas: result[gasCur].gas,
            tokens: result[gasCur].tokens,
        };
    }
}
exports.NetworkApi = NetworkApi;
